import { useRef, useEffect, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import styles from '../InterventionGraphEditor.module.css';

interface FloatingPopoverProps {
  open: boolean;
  triggerRect: DOMRect | null;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}

export const FloatingPopover = ({
  open,
  triggerRect,
  onClose,
  children,
  width = 320
}: FloatingPopoverProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (panelRef.current?.contains(target)) return;
      if (target.closest('[data-floating-child]')) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open || !triggerRect) return null;

  const spaceBelow = window.innerHeight - triggerRect.bottom - 8;
  const spaceAbove = triggerRect.top - 8;
  const useBelow = spaceBelow >= 180 || spaceBelow >= spaceAbove;
  const left = Math.min(triggerRect.left, window.innerWidth - width - 8);

  const posStyle: CSSProperties = useBelow
    ? { top: triggerRect.bottom + 6, maxHeight: Math.max(spaceBelow, 120) }
    : { bottom: window.innerHeight - triggerRect.top + 6, maxHeight: Math.max(spaceAbove, 120) };

  return createPortal(
    <div
      ref={panelRef}
      className={styles.popover}
      style={{ position: 'fixed', left, width, ...posStyle }}
    >
      {children}
    </div>,
    document.body
  );
};
